import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import store from "store/store";

import SearchDropdown from "@cayuse/shared-react.components.search-dropdown";
import Button from "@cayuse/shared-react.components.button";
import { PreventLeaveDialog } from "@cayuse/shared-react.components.prevent-leave-page-dialog";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import { Icon } from "@cayuse/shared-react.components.icon";
import { notify } from "@cayuse/shared-react.components.banner-notification";

import { proposalSelectors, tenantSelectors } from "store/selectors";
import { setFormEntity } from "store/actions/form";

import { api } from "services";
import {
    s2sErrorHandler,
    getS2SInstanceURL,
    notifyError,
    queryOpportunities,
    getS2sItems,
    getS2sConfig,
    S2S_RECORD_KEY,
    buildCardItem,
    getProposalData,
} from "utils";
import { useFormAccesses, useDictionary } from "hooks";
import { FORM_EVENTS } from "components/FormResponse/hooks/useFormSave";

import {
    COLUMN_HEADER_KEYS,
    PROPOSAL_REVIEW_STATUS,
    DEFAULT_SEARCH_OPTIONS,
} from "globals/constants";

import InfoCard from "components/common/InfoCard/InfoCard";
import Tooltip from "components/Tooltip";

import styles from "./styles.css";

const DICTIONARY = {
    opportunitySearchLabel: "Proposals (S2S) Opportunity Search",
    opportunityPlaceholder: "Search by opportunity title or opportunity number",
    searchingMessage: "Loading...",
    unableLink: "Unable to link proposal. Please try again later.",
    unableUnlink: "Unable to unlink proposal. Please try again later.",
    unableCreateProposal:
        "Unable to create proposal due to invalid data. Please reach out to Cayuse support for assistance.",
    createNewLabel: "Create new Grants.gov proposal",
    noAccess:
        "The ability to use this form object requires an S2S user role. Please work with an administrator to give you an applicable role if necessary.",
    noS2SProduct: "Proposals (S2S) is not provisioned on this tenant.",
    serviceUnavailable:
        "The Proposals (S2S) service is unavailable or not yet configured.",
    contactAdminstrator:
        "Please contact an administrator if the issue persists.",
    errorGeneric: "An error occurred loading the Proposals (S2S) service.",
    linkedHeader: "Proposals (S2S) Record",
    partlyAccess:
        "You don’t have sufficient access to the linked S2S Proposal to see all data fields or unlink the proposal. Please work with an administrator to give you access to the applicable S2S Proposal if necessary.",
    noProposalID: "No Proposal ID found",
    successSaveMsg: "Proposals (S2S) record created",
    createNewS2SRecord: "Create New Proposal (S2S) Record",
};

const getDefaultRenderLabel = ({ tooltip, text }) => ({
    content: <span title={tooltip}>{text}</span>,
});

const ProposalsS2SRecord = (props) => {
    const {
        Components,
        payload: { proposalId },
        config,
    } = props;

    const [s2sURL, setS2sURL] = useState();
    const [proposal, setProposal] = useState();
    const [s2sLinks, setS2sLinks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchField, setSearchField] = useState();
    const [errorMessage, setErrorMessage] = useState();
    const [unsavedChanges, setUnsavedChanges] = useState();
    const [hasErrorLoading, setHasErrorLoading] = useState(false);
    const [proposalsS2SRecord, setProposalsS2SRecord] = useState();
    const [prefetchedOptions, setPrefetchedOptions] = useState(
        DEFAULT_SEARCH_OPTIONS
    );

    const formAccesses = useFormAccesses(
        COLUMN_HEADER_KEYS.PROPOSAL,
        proposalId
    );
    const dictionary = useDictionary("s2sRecord");
    const { statusName } = useSelector(proposalSelectors.proposal);
    const tenantId = useSelector(tenantSelectors.tenantId);
    const header = config.label;
    const isRequired = config.isRequired;

    const selectConfig = useMemo(
        () => getS2sConfig(s2sLinks, config, dictionary),
        [s2sLinks, config]
    );

    const onChange = (value) => {
        props.onEvent(FORM_EVENTS.FOCUS, { $path: config.$path }, {});
        props.onChange(value);
        props.onEvent(FORM_EVENTS.BLUR, { $path: config.$path }, {});
    };

    const createNewS2SRecord = async () => {
        setIsLoading(true);
        try {
            const referenceIdUrl = await api.s2s.createReferenceId(proposalId);

            const body = await getProposalData(
                proposal,
                prefetchedOptions,
                referenceIdUrl
            );
            await api.s2s.createProposal(body, s2sURL);

            const { s2sLink } = await api.s2s.get(proposalId);
            if (!s2sLink) {
                notifyError(
                    dictionary.unableCreateProposal ??
                        DICTIONARY.unableCreateProposal,
                    s2sLink
                );
                return;
            }

            const record = buildCardItem(s2sLink);
            setProposalsS2SRecord(record);
            notify(dictionary.successSaveMsg ?? DICTIONARY.successSaveMsg, {
                type: "success",
                autoClose: true,
            });
        } catch (error) {
            notifyError(
                dictionary.unableCreateProposal ??
                    DICTIONARY.unableCreateProposal,
                error
            );
        } finally {
            setIsLoading(false);
            setUnsavedChanges(false);
        }
    };

    const navigatToS2s = () => {
        api.s2s.createReferenceId(proposalId).then((createUrl) => {
            window.open(createUrl);
        });
    };

    const linkS2SRecord = (s2sRecord) => {
        if (!!s2sRecord) {
            setIsLoading(true);
            api.s2s
                .createLink({
                    proposalId,
                    s2sProposalId: s2sRecord.s2sProposalId,
                })
                .then((result) => {
                    const record = buildCardItem(result?.s2sLink);
                    setProposalsS2SRecord(record);
                    setFormEntity({ documentVersion: result.documentVersion });
                })
                .catch((e) => {
                    notifyError(
                        dictionary.unableLink ?? DICTIONARY.unableLink,
                        e
                    );
                })
                .finally(() => setIsLoading(false));
        }
    };

    const unlinkS2SRecord = () => {
        setIsLoading(true);
        api.s2s.unlink(proposalId).then((response) => {
            setFormEntity({ documentVersion: response.documentVersion });
            api.s2s
                .get(proposalId)
                .then((data) => {
                    if (data.s2sLink) {
                        notifyError(
                            dictionary.unableUnlink ?? DICTIONARY.unableUnlink,
                            data
                        );
                    } else if (isNonEmptyArray(data.s2sLinks)) {
                        setS2sLinks(data.s2sLinks);
                        setPrefetchedOptions(DEFAULT_SEARCH_OPTIONS);
                        setProposalsS2SRecord(null);
                        setSearchField(null);
                    }
                })
                .catch((e) => {
                    notifyError(
                        dictionary.unableUnlink ?? DICTIONARY.unableUnlink,
                        e
                    );
                })
                .finally(() => setIsLoading(false));
        });
    };

    const search = async (searchText) => {
        setPrefetchedOptions([]);
        return queryOpportunities(searchText, s2sURL);
    };

    const onOpportunityChange = (value, newOpportunity) => {
        setPrefetchedOptions(newOpportunity);
        setSearchField(value);
        setUnsavedChanges(true);
    };

    const s2sRecordToCardItems = useMemo(
        () => getS2sItems(proposalsS2SRecord, S2S_RECORD_KEY),
        [proposalsS2SRecord]
    );

    const renderContent = useCallback(async () => {
        const url = await getS2SInstanceURL(tenantId);
        if (!url) {
            setHasErrorLoading(true);
            setErrorMessage(dictionary.noS2SProduct ?? DICTIONARY.noS2SProduct);
            setIsLoading(false);
            return;
        }
        setS2sURL(url);
        api.proposal.get(proposalId).then((response) => setProposal(response));
        api.s2s
            .get(proposalId)
            .then((data) => {
                if (data.s2sLink) {
                    const record = buildCardItem(data.s2sLink);
                    setProposalsS2SRecord(record);
                } else if (isNonEmptyArray(data.s2sLinks)) {
                    setS2sLinks(data.s2sLinks);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                const serviceError = s2sErrorHandler(
                    error,
                    dictionary,
                    DICTIONARY
                );
                setHasErrorLoading(true);
                setErrorMessage(serviceError);
            })
            .finally(() => setIsLoading(false));
    }, [proposalId]);

    useEffect(() => {
        if (proposalId) {
            onChange(proposalsS2SRecord);
        }
    }, [proposalsS2SRecord, proposalId]);

    useEffect(() => {
        if (proposalId) {
            renderContent();
        } else {
            setHasErrorLoading(true);
            setErrorMessage(dictionary.noProposalID ?? DICTIONARY.noProposalID);
            setIsLoading(false);
        }
    }, [renderContent, proposalId]);

    return (
        <div className={styles.s2s}>
            <PreventLeaveDialog prevent={unsavedChanges} />
            <Tooltip
                id={selectConfig.id}
                tooltipContent={
                    selectConfig.tooltip || selectConfig.extraSettings.tooltip
                }
            >
                <h3 className={styles.extraSettingsTooltip}>
                    {header}
                    {isRequired && <span className={styles.requiredAsterisk}>*</span>}
                </h3>
            </Tooltip>
            {isLoading ? (
                <LoadingIndicator />
            ) : hasErrorLoading ? (
                <div className={styles.error}>{errorMessage}</div>
            ) : (
                <>
                    {proposalsS2SRecord && (
                        <>
                            <InfoCard
                                className={styles.card}
                                items={s2sRecordToCardItems}
                                onClose={unlinkS2SRecord}
                                canEdit={config.enabled && proposalsS2SRecord.url}
                            />
                            {!proposalsS2SRecord.url && (
                                <div className={styles.line}>
                                    {dictionary?.partlyAccess ??
                                        DICTIONARY.partlyAccess}
                                </div>
                            )}
                        </>
                    )}

                    {!proposalsS2SRecord && (
                        <div className={styles.form}>
                            <Components.select
                                {...props}
                                config={selectConfig}
                                onChange={linkS2SRecord}
                                className={styles.line}
                            />
                            <label className={styles.line}>OR</label>
                            <br />
                            <label className={styles.line}>
                                {dictionary?.createNewLabel ??
                                    DICTIONARY.createNewLabel}
                            </label>
                            <div
                                className={`${styles.searchDropdown} ${styles.line}`}
                            >
                                <label>
                                    {dictionary?.opportunitySearchLabel ??
                                        DICTIONARY.opportunitySearchLabel}
                                </label>
                                <SearchDropdown
                                    value={searchField}
                                    search={(options) => options}
                                    selection
                                    selectOnNavigation={false}
                                    forceSelection={false}
                                    selectOnBlur={false}
                                    onGetDataPromise={search}
                                    disabled={!config.enabled}
                                    searchingMessage={
                                        dictionary.searchingMessage ??
                                        DICTIONARY.searchingMessage
                                    }
                                    placeholder={
                                        dictionary.opportunityPlaceholder ??
                                        DICTIONARY.opportunityPlaceholder
                                    }
                                    onChange={onOpportunityChange}
                                    renderLabel={getDefaultRenderLabel}
                                    prefetchedOptions={prefetchedOptions}
                                    multiple={false}
                                    narrowCriteriaHint={{ show: true }}
                                    minimumCharacters={3}
                                    fieldLabel={dictionary?.opportunitySearchLabel ?? DICTIONARY.opportunitySearchLabel}
                                    loadingIcon={
                                        <span
                                            className="loading-indicator"
                                            aria-hidden="true"
                                        >
                                            <span className="dot-flashing" />
                                        </span>
                                    }
                                    icon={
                                        <span
                                            className="caret-icon"
                                            aria-hidden="true"
                                        >
                                            <svg
                                                height="20"
                                                width="20"
                                                viewBox="0 0 20 20"
                                                aria-hidden="true"
                                                focusable="false"
                                            >
                                                <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
                                            </svg>
                                        </span>
                                    }
                                    clearIcon={
                                        <span className="clear-icon">
                                            <svg
                                                height="20"
                                                width="20"
                                                viewBox="0 0 20 20"
                                                aria-hidden="true"
                                                focusable="false"
                                            >
                                                <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
                                            </svg>
                                        </span>
                                    }
                                />
                            </div>
                            <div className={styles.line}>
                                <Button
                                    className={config.enabled ? "" : styles.disabled}
                                    primary
                                    onClick={createNewS2SRecord}
                                    disabled={!config.enabled || !searchField}
                                >
                                    {dictionary?.createNewS2SRecord ||
                                        DICTIONARY.createNewS2SRecord}
                                </Button>
                            </div>
                            <label className={styles.line}>OR</label>
                            <div className={styles.line}>
                                {config.enabled && (
                                    <a
                                        className={styles.link}
                                        onClick={navigatToS2s}
                                    >
                                        Go to Proposals (S2S){" "}
                                        <Icon name="arrow-up-right-from-square" />
                                    </a>
                                )}
                                {!config.enabled && (
                                    <span className={styles.disabledLink}>
                                        Go to Proposals (S2S){" "}
                                        <Icon name="arrow-up-right-from-square" />
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ProposalsS2SRecord;
